import React, { useEffect, useMemo, useState } from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { normalizeStripeClientSecret } from "../../../shared/stripeUtil.js";

/**
 * Mounts Stripe Embedded Checkout — same payment methods as Hosted Checkout
 * (Link, Apple Pay, Google Pay, cards, …) controlled by the Stripe Dashboard.
 */
export function StripeEmbeddedCheckout({
  publishableKey,
  clientSecret,
  onComplete,
}: {
  publishableKey: string;
  clientSecret: string;
  /** Fired when Embedded Checkout finishes (requires redirect_on_completion: never). */
  onComplete?: () => void;
}) {
  const normalizedSecret = useMemo(() => normalizeStripeClientSecret(clientSecret), [clientSecret]);
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setInitError(null);
    setStripe(null);
    void loadStripe(publishableKey).then((instance) => {
      if (cancelled) return;
      if (!instance) {
        console.error("[zenpay] stripe.js.load_failed", { publishableKeyPrefix: publishableKey.slice(0, 12) });
        setInitError("stripe_js_load_failed");
        return;
      }
      setStripe(instance);
    }).catch((err) => {
      if (cancelled) return;
      console.error("[zenpay] stripe.js.load_error", err);
      setInitError(err instanceof Error ? err.message : String(err));
    });
    return () => {
      cancelled = true;
    };
  }, [publishableKey]);

  if (initError) {
    return (
      <div className="text-sm text-danger py-8 text-center space-y-2">
        <p>Não foi possível carregar o formulário de pagamento.</p>
        {import.meta.env.DEV && (
          <p className="text-xs text-ink-2 font-mono break-all">{initError}</p>
        )}
      </div>
    );
  }

  if (!stripe) {
    return <div className="text-sm text-ink-2 py-12 text-center">Carregando pagamento…</div>;
  }

  return (
    <EmbeddedCheckoutProvider
      stripe={Promise.resolve(stripe)}
      options={{
        clientSecret: normalizedSecret,
        ...(onComplete ? { onComplete } : {}),
      }}
    >
      <EmbeddedCheckout className="stripe-embedded-checkout" />
    </EmbeddedCheckoutProvider>
  );
}
